import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';
import styled from '@emotion/styled';
import {
  FormRow,
  FormLabel,
  FormFieldWrapper,
  FormRowProps
} from '../form-input';
import {
  FilePicker,
  FilePickerRef,
  getCdnPath,
  getS3Path,
  getWidgetCdnPath,
  useErrorNotification,
  useRetailerFileUpload
} from '../../framework';
import { Select, MenuItem } from '@mui/material';

const PersonaSelect = styled(Select<string>)`
  .MuiSelect-select {
    display: flex;
    align-items: center;
    position: relative;
    padding-left: 176px;
    overflow: hidden;
    height: 80px;

    img {
      position: absolute;
      top: 0;
      left: 8px;
      max-width: 160px;
      height: 100%;
    }
  }
`;

const PersonaRow = styled(MenuItem)`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: 176px;
  overflow: hidden;
  height: 80px;

  img {
    position: absolute;
    top: 0;
    left: 8px;
    max-width: 160px;
    height: 100%;
  }
`;

const personas = [
  {
    name: 'Kate',
    url: `${getWidgetCdnPath()}/personas/kate.webp`
  },
  {
    name: 'Ashley',
    url: `${getWidgetCdnPath()}/personas/ashley.webp`
  },
  {
    name: 'Tasha',
    url: `${getWidgetCdnPath()}/personas/tasha.webp`
  },
  {
    name: 'Chris',
    url: `${getWidgetCdnPath()}/personas/chris.webp`
  },
  {
    name: 'Mark',
    url: `${getWidgetCdnPath()}/personas/mark.webp`
  },
  {
    name: 'Olli',
    url: `${getWidgetCdnPath()}/personas/olli.webp`
  },
  {
    name: 'Zack',
    url: `${getWidgetCdnPath()}/personas/zack.webp`
  }
];

interface HeaderImageFieldProps extends FormRowProps {
  value?: string;
  onChange: (value?: string) => void;
  retailerSlug: string;
}

export function HeaderImageField(props: HeaderImageFieldProps) {
  const { value, onChange, retailerSlug, ...formRowProps } = props;

  const filePickerRef = useRef<FilePickerRef>(null);

  const { t } = useTranslation();
  const { displayErrors } = useErrorNotification();
  const { put, error } = useRetailerFileUpload({
    slug: retailerSlug,
    fileTag: `custom-header-${Date.now()}`
  });

  const onPersonaChosen = (e: SelectChangeEvent<string>) => {
    // don't emit here because it emits on handleFileSelected
    if (e.target.value === 'custom') return;

    onChange(e.target.value);
  };

  const handleFileSelected = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    const result = await put(file);

    onChange(result?.cdnUrl);
  };

  const selectedValue = useMemo(() => {
    // default to Kate
    if (!value) {
      return `${getWidgetCdnPath()}/personas/kate.webp`;
    }

    if (personas.some(p => p.url === value)) {
      return value;
    }

    return 'custom';
  }, [value]);

  const customImageSrc = useMemo(
    () => value?.replace(getCdnPath(), getS3Path()) ?? '',
    [value]
  );

  useEffect(() => {
    if (error) {
      displayErrors([error]);
    }
  }, [error, displayErrors]);

  return (
    <FormRow {...formRowProps}>
      <FormLabel>{t('headerImage')}</FormLabel>
      <FormFieldWrapper>
        <PersonaSelect
          fullWidth
          onChange={onPersonaChosen}
          value={selectedValue}
        >
          {personas.map(persona => (
            <PersonaRow key={persona.name} value={persona.url}>
              <img src={persona.url} />
              {persona.name}
            </PersonaRow>
          ))}
          <PersonaRow
            value="custom"
            onClick={() => filePickerRef.current?.open()}
          >
            <img src={customImageSrc} />
            {t('custom')}
          </PersonaRow>
        </PersonaSelect>
        <FilePicker
          ref={filePickerRef}
          onFileSelected={handleFileSelected}
          style={{ display: 'none' }}
          accept={'image/*'}
        />
      </FormFieldWrapper>
    </FormRow>
  );
}
